import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import WarehouseTable from "../components/WarehouseTable";
import { getPageName } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useWarehouse } from "../../../apis/warehouse/WarehouseProvider";
import Paginacao from "../../../types/paginacao";
import Warehouse from "../../../types/warehouse";
import { FaEdit } from "react-icons/fa";

const WarehouseView: React.FC = () => {
    const { t } = useTranslation();
    const { warehouses, apiResponse, fetchWarehouse } = useWarehouse();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 20,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 20
    } as Paginacao);
    
    useEffect(() => {
        const newPaginacao = {
            ...paginacao, 
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize
        };
        setPaginacao(newPaginacao);
        fetchWarehouse(newPaginacao);
    }, [paginationModel]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });
    
            navigate(location.pathname, { replace: true });
    
            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);
     
    const navItems: NavBarItem[] = [
        { label: t('warehouse.labels.home'), route: "/", iconName: "house-door" },
        { label: t('warehouse.labels.new'), route: "/Warehouse/create", iconName: "plus-circle" },
    ];

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: t('warehouse.fields.id'),
            width: 80,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                    onClick={() => navigate(`/warehouse/${params.row.id}`)}>
                    <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                    {params.row.id}
                </span>
            ),
        },
        { field: "cnpj", headerName: t('warehouse.fields.cnpj'), width: 200 },
        { field: "identification", headerName: t('warehouse.fields.identification'), width: 200 },
        { field: "name", headerName: t('warehouse.fields.name'), width: 200 },
        { field: "sz", headerName: t('warehouse.fields.sz'), width: 200 },
    ];

    return (
         <Container fluid style={{ height: 'calc(100vh - 200px)', overflow: 'auto'}}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("warehouse")}</h2>
            <div className="mb-3"></div>
            <div>
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
            <WarehouseTable
                rows={warehouses || [] as Warehouse[]}
                columns={columns}
                getRowClassName={(params: any) => {
                    const color = params.row.color;
                    const font_color = params.row.fontColor;

                    let className = "";
                    if (color && /^([0-9A-F]{6})$/i.test(color)) {
                        className += `row-color-${color} `;
                    }
                    if (font_color && /^([0-9A-F]{6})$/i.test(font_color)) {
                        className += `row-font-color-${font_color} `;
                    }
                    
                    if (className && className.length > 0) {
                        return className;
                    }
                    return params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row";
                }}
                apiResponse={apiResponse} 
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
        </Container>
    );
};

export default WarehouseView;